/**
 * ConcreteCalc — slabs, footings, sonotubes, and steps to cubic yards.
 *
 * Per research pain point #4: default waste is 10%, NEVER 5% — running
 * short on a pour means a cold joint, which is catastrophic. Suppliers
 * universally recommend ordering over.
 *
 * Form options come from the research line items. The "magic 81" shortcut
 * is just the slab math expressed differently (sqft / 81 = cu yd at 4").
 * We surface it as a sanity-check hint.
 */

import React, { useMemo, useState } from 'react';
import type { Clave } from '../../../../i18n/textos';
import { useIdioma } from '../../../../i18n/idioma';
import toast from 'react-hot-toast';
import { CalculatorProps } from '../registry';
import { priceLine } from '../../../../utils/lineItemPricing';
import { NumberField, SelectField, OutputCard, OutputRow, AddToEstimateButton, SectionHeader } from '../CalculatorPrimitives';
import type { EstimateItem } from '../../../../types';

type FormKey = 'slab' | 'footing' | 'sonotube' | 'steps';

/* La llave (`slab`, `footing`…) es el dato del cálculo; solo la etiqueta es texto. */
const FORM_LABELS: Record<FormKey, Clave> = {
  slab: 'estimado.concreto.forma.slab',
  footing: 'estimado.concreto.forma.footing',
  sonotube: 'estimado.concreto.forma.sonotube',
  steps: 'estimado.concreto.forma.steps',
};

const READY_MIX_CATALOG_ID = 'landscaping.ready-mix-concrete-cu-yd';
const BAG_CATALOG_ID = 'landscaping.concrete-mix-bag-80lb';
const WASTE_PCT = 10;
const CU_FT_PER_CU_YD = 27;
const READY_MIX_THRESHOLD_CU_YD = 1; // Below 1 cu yd, contractors typically bag-mix.

const ConcreteCalc: React.FC<CalculatorProps> = ({ onAddTemplateItems, pricebookByHint, targetMarginPercent, materialMarkupPercent }) => {
    const { t } = useIdioma();
  const [form, setForm] = useState<FormKey>('slab');

  // Slab / footing inputs
  const [length, setLength] = useState(0);
  const [width, setWidth] = useState(0);
  const [thicknessIn, setThicknessIn] = useState(4);

  // Sonotube inputs
  const [diameterIn, setDiameterIn] = useState(12);
  const [heightFt, setHeightFt] = useState(3);
  const [tubeCount, setTubeCount] = useState(1);

  // Steps inputs
  const [stepCount, setStepCount] = useState(3);
  const [stepRunIn, setStepRunIn] = useState(11);
  const [stepRiseIn, setStepRiseIn] = useState(7);
  const [stepWidthFt, setStepWidthFt] = useState(4);

  const calc = useMemo(() => {
    let cuFt = 0;
    let basis = '';
    if (form === 'slab' || form === 'footing') {
      cuFt = (length || 0) * (width || 0) * ((thicknessIn || 0) / 12);
      basis = t('estimado.concreto.base_losa', { length, width, thicknessIn });
    } else if (form === 'sonotube') {
      const radiusFt = (diameterIn || 0) / 12 / 2;
      cuFt = Math.PI * radiusFt * radiusFt * (heightFt || 0) * (tubeCount || 0);
      basis = t('estimado.concreto.base_sonotube', { tubeCount, diameterIn, heightFt });
    } else if (form === 'steps') {
      // Each step is a wedge — the cumulative volume is the sum of rise × run × width.
      // Simplified: total volume = stepCount × (run/12 × rise/12 × width × stepNumber) accumulated.
      // For practical estimating we use the prismatic approx: total cu ft ≈ steps × run × rise × width / 144 × (steps + 1) / 2.
      const runFt = (stepRunIn || 0) / 12;
      const riseFt = (stepRiseIn || 0) / 12;
      cuFt = (stepCount || 0) * runFt * riseFt * (stepWidthFt || 0) * ((stepCount || 0) + 1) / 2;
      basis = t('estimado.concreto.base_escalones', { stepCount, stepRunIn, stepRiseIn, stepWidthFt });
    }
    const cuYdRaw = cuFt / CU_FT_PER_CU_YD;
    const cuYd = cuYdRaw * (1 + WASTE_PCT / 100);
    const orderedCuYd = Math.ceil(cuYd * 4) / 4; // ready-mix orders go in 0.25 yd increments
    const sqFt = (form === 'slab' || form === 'footing') ? (length || 0) * (width || 0) : 0;
    const magic81 = (form === 'slab' && thicknessIn === 4 && sqFt > 0) ? sqFt / 81 : null;
    const bags80lb = cuFt > 0 ? Math.ceil(cuFt * (1 + WASTE_PCT / 100) / 0.6) : 0; // an 80lb bag ≈ 0.6 cu ft
    const bags60lb = cuFt > 0 ? Math.ceil(cuFt * (1 + WASTE_PCT / 100) / 0.45) : 0;
    const bags40lb = cuFt > 0 ? Math.ceil(cuFt * (1 + WASTE_PCT / 100) / 0.30) : 0;
    const weightLbs = cuYd * 4000;
    const useReadyMix = cuYd >= READY_MIX_THRESHOLD_CU_YD;
    return { cuFt, cuYdRaw, cuYd, orderedCuYd, magic81, bags80lb, bags60lb, bags40lb, weightLbs, useReadyMix, basis };
  }, [form, length, width, thicknessIn, diameterIn, heightFt, tubeCount, stepCount, stepRunIn, stepRiseIn, stepWidthFt]);

  const canAdd = calc.cuYd > 0;

  const handleAdd = () => {
    // El concreto es MATERIAL: dial de MARKUP (sobre el COSTO), no de margen.
    // Con la fórmula de margen, un ready-mix a $168/CY salía a $240,00 en vez
    // de $201,60 —sobre 22 CY son $844,80 de diferencia en una sola fila.
    //
    // El precio que devuelve `priceLine` ES el `baseRate`: nace con k = 1, o
    // sea "exactamente los diales del taller". Se estampa junto al `rate`
    // porque si la fila entrara sin él, el solver de margen neto tendría que
    // adivinarlo con `baseRateOf`, y una partida agregada DESPUÉS de un ajuste
    // del dial quedaría una generación por detrás de una idéntica agregada
    // antes (Parte A.6.6 del plan v2).
    const dials = { targetMarginPercent, materialMarkupPercent };
    let item: EstimateItem;
    if (calc.useReadyMix) {
      const cost = pricebookByHint.get(READY_MIX_CATALOG_ID) ?? 0;
      const { rate, total, markupPercent, marginPercent } = priceLine('Material', cost, calc.orderedCuYd, dials);
      item = {
        id: Math.random().toString(36).substr(2, 9),
        type: 'Material',
        description: `${t('estimado.concreto.partida_readymix')} — ${calc.basis}`,
        quantity: calc.orderedCuYd,
        unit: 'cu yd',
        cost,
        rate,
        baseRate: rate,
        markupPercent,
        marginPercent,
        total,
        sourcePricebookId: READY_MIX_CATALOG_ID,
        calcBasis: t('estimado.concreto.basis_desperdicio', { basis: calc.basis, waste: WASTE_PCT }),
        isOverridden: false,
      };
    } else {
      const cost = pricebookByHint.get(BAG_CATALOG_ID) ?? 0;
      const { rate, total, markupPercent, marginPercent } = priceLine('Material', cost, calc.bags80lb, dials);
      item = {
        id: Math.random().toString(36).substr(2, 9),
        type: 'Material',
        description: `${t('estimado.concreto.partida_sacos')} — ${calc.basis}`,
        quantity: calc.bags80lb,
        unit: 'bag',
        cost,
        rate,
        baseRate: rate,
        markupPercent,
        marginPercent,
        total,
        sourcePricebookId: BAG_CATALOG_ID,
        calcBasis: t('estimado.concreto.basis_desperdicio_total', { basis: calc.basis, waste: WASTE_PCT, cuYd: calc.cuYd.toFixed(2) }),
        isOverridden: false,
      };
    }
    onAddTemplateItems([item], { id: 'calc.concrete', name: t('estimado.concreto.plantilla_nombre', { basis: calc.basis }) });
    toast.success(calc.useReadyMix
      ? t('estimado.concreto.toast_readymix', { cuYd: calc.orderedCuYd })
      : t('estimado.concreto.toast_sacos', { bags: calc.bags80lb }));
  };

  return (
    <>
      <SelectField
        label={t('estimado.concreto.campo_forma')}
        value={form}
        // Wrapped rather than passing setForm directly: a bare setState widens
        // SelectField's generic to `string` and breaks the typecheck.
        onChange={(v) => setForm(v)}
        options={(['slab', 'footing', 'sonotube', 'steps'] as FormKey[]).map(k => ({ value: k, label: t(FORM_LABELS[k]) }))}
      />

      {(form === 'slab' || form === 'footing') && (
        <>
          <SectionHeader title={t('estimado.concreto.seccion_medidas')} />
          <div className="grid grid-cols-3 gap-3">
            <NumberField label={t('estimado.concreto.campo_largo')} unit="ft" value={length} onChange={setLength} />
            <NumberField label={t('estimado.concreto.campo_ancho_losa')} unit="ft" value={width} onChange={setWidth} />
            <NumberField label={t('estimado.concreto.campo_grueso')} unit="in" value={thicknessIn} onChange={setThicknessIn} />
          </div>
        </>
      )}

      {form === 'sonotube' && (
        <>
          <SectionHeader title={t('estimado.concreto.seccion_tubo')} />
          <div className="grid grid-cols-3 gap-3">
            <NumberField label={t('estimado.concreto.campo_diametro')} unit="in" value={diameterIn} onChange={setDiameterIn} />
            <NumberField label={t('estimado.concreto.campo_alto')} unit="ft" value={heightFt} onChange={setHeightFt} />
            <NumberField label={t('estimado.concreto.campo_cantidad')} unit={t('estimado.concreto.unidad_tubos')} value={tubeCount} onChange={setTubeCount} step="1" min={1} />
          </div>
        </>
      )}

      {form === 'steps' && (
        <>
          <SectionHeader title={t('estimado.concreto.seccion_escalon')} />
          <div className="grid grid-cols-2 gap-3">
            <NumberField label={t('estimado.concreto.campo_num_escalones')} unit={t('estimado.concreto.unidad_cada_uno')} value={stepCount} onChange={setStepCount} step="1" min={1} />
            <NumberField label={t('estimado.concreto.campo_ancho_losa')} unit="ft" value={stepWidthFt} onChange={setStepWidthFt} />
            <NumberField label={t('estimado.concreto.campo_run')} unit="in" value={stepRunIn} onChange={setStepRunIn} />
            <NumberField label={t('estimado.concreto.campo_rise')} unit="in" value={stepRiseIn} onChange={setStepRiseIn} />
          </div>
        </>
      )}

      <SectionHeader title={t('estimado.concreto.seccion_ocupas')} />
      <OutputCard>
        <OutputRow
          label={calc.useReadyMix ? t('estimado.concreto.pedirReadyMix') : t('estimado.concreto.mezclarEnObra')}
          value={calc.useReadyMix ? `${calc.orderedCuYd} cu yd` : t('estimado.concreto.salida_sacos_valor', { bags: calc.bags80lb })}
          emphasis="primary"
          hint={calc.useReadyMix
            ? t('estimado.concreto.hint_readymix', { waste: WASTE_PCT })
            : t('estimado.concreto.hint_sacos', { waste: WASTE_PCT, cuYd: calc.cuYd.toFixed(2) })}
        />
        {calc.cuYd > 0 && (
          <>
            <OutputRow label={t('estimado.concreto.salida_yardas')} value={`${calc.cuYd.toFixed(2)} cu yd`} />
            {calc.magic81 !== null && (
              <OutputRow label={t('estimado.concreto.salida_magic81')} value={`${calc.magic81.toFixed(2)} cu yd`} emphasis="muted" hint={t('estimado.concreto.hint_magic81')} />
            )}
            <OutputRow label={t('estimado.concreto.salida_sacos_60')} value={`${calc.bags60lb}`} emphasis="muted" />
            <OutputRow label={t('estimado.concreto.salida_sacos_40')} value={`${calc.bags40lb}`} emphasis="muted" />
            <OutputRow label={t('estimado.concreto.salida_peso')} value={`${Math.round(calc.weightLbs).toLocaleString()} lbs`} emphasis="muted" />
          </>
        )}
      </OutputCard>

      <AddToEstimateButton
        label={canAdd
          ? (calc.useReadyMix ? t('estimado.concreto.boton_readymix', { cuYd: calc.orderedCuYd }) : t('estimado.concreto.boton_sacos', { bags: calc.bags80lb }))
          : t('estimado.concreto.boton_sin_medidas')}
        onClick={handleAdd}
        disabled={!canAdd}
      />
    </>
  );
};

export default ConcreteCalc;
